'use client'

/**
 * src/components/DataExportButton.tsx
 *
 * Settings → "Your data" — fetches GET /api/me/export, which streams
 * back a JSON file with Content-Disposition: attachment. Browsers
 * prompt to save it.
 *
 * The endpoint is rate-limited to 5/hour per user; we surface the
 * server's error message verbatim if we hit the cap.
 */

import { useState } from 'react'
import { Button } from '@/components/ui/button'

export default function DataExportButton() {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function onClick() {
    setBusy(true)
    setError(null)
    try {
      const res = await fetch('/api/me/export', { cache: 'no-store' })
      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        setError(
          typeof data?.message === 'string'
            ? data.message
            : 'Could not export your data. Please try again.',
        )
        return
      }
      // Stream the response into a Blob and trigger a download.
      const blob = await res.blob()
      const filename =
        res.headers.get('Content-Disposition')?.match(/filename="([^"]+)"/i)?.[1] ??
        'unlimitedtests-export.json'
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch {
      setError('Network error. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-foreground">Download my data</div>
        <p className="mt-0.5 text-sm text-muted-foreground">
          A JSON file with your account and settings, tests and drills with your answers, reviews,
          bookmarks, study-plan activity, your main Sage conversation, and security-log entries
          about you. Not yet included: topic-practice answers, usage events, chats about a single
          question, ACT activity, or uploaded score reports — email us for a complete copy.
        </p>
        {error && <p className="mt-1 text-sm text-rose-700 dark:text-rose-300">{error}</p>}
      </div>
      <Button
        type="button"
        onClick={onClick}
        disabled={busy}
        variant="outline"
        className="shrink-0 border-brand/40 text-brand-ink hover:bg-brand/10 dark:text-brand-ink"
      >
        {busy ? 'Preparing…' : 'Download'}
      </Button>
    </div>
  )
}
